import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    paletteColors,
    Theme,
    Typography,
    useMediaQuery,
} from '@evaneos/b2b-ui';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';
import NotificationsIcon from '@evaneos/b2b-ui/icons/NotificationsOutlined';
import NotificationsActiveIcon from '@evaneos/b2b-ui/icons/NotificationsActiveOutlined';
import { useIntl } from 'react-intl';
import { QueryStatus } from '@tanstack/react-query';

import { Notification } from '../../../resources/notifications/types';

import { NotificationsContent } from './NotificationsContent';

type NotificationsDrawerProps = {
    notifications: Notification[] | undefined;
    notificationsStatus: QueryStatus;
    areNotificationsLoading: boolean;
};

export const NotificationsDrawer = ({
    notifications = [],
    notificationsStatus,
    areNotificationsLoading,
}: NotificationsDrawerProps) => {
    const { formatMessage } = useIntl();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const unreadNotificationsCount = notifications.filter(
        (notification) => !notification.read,
    ).length;
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                aria-labelledby="notifications_btn_label"
                sx={{
                    gap: 1,
                    marginLeft: 'auto',
                    alignSelf: 'center',
                    minWidth: isMobile ? 'auto' : undefined,
                    paddingX: isMobile ? 1 : undefined,
                }}
            >
                {unreadNotificationsCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                <Box
                    component="span"
                    id="notifications_btn_label"
                    sx={{ display: isMobile ? 'none' : 'flex', gap: 1, alignItems: 'center' }}
                >
                    {formatMessage({ id: 'homepage.notifications.panel.button' })}
                    <Chip
                        component="strong"
                        variant="filled"
                        label={unreadNotificationsCount}
                        size="small"
                        sx={{
                            backgroundColor: paletteColors.blue[100],
                        }}
                    />
                </Box>
            </Button>
            <Drawer
                variant="persistent"
                anchor="right"
                open={notificationsOpen}
                onClose={() => setNotificationsOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: isMobile ? '100%' : '450px',
                        position: 'absolute',
                        zIndex: 1000,
                    },
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        padding={3}
                        paddingBottom={1.5}
                        paddingRight={{ xs: 0.5, md: 2 }}
                        borderBottom="1px solid"
                        borderColor="divider"
                    >
                        <Typography variant="h3" fontWeight={800}>
                            {formatMessage(
                                { id: 'homepage.notifications.title' },
                                { count: unreadNotificationsCount },
                            )}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={() => setNotificationsOpen(false)}
                            sx={{
                                color: 'icon.primary',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flex: 1, overflowY: 'auto' }}>
                        <NotificationsContent
                            notifications={notifications}
                            notificationsStatus={notificationsStatus}
                            areNotificationsLoading={areNotificationsLoading}
                        />
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
